import {ReactNode, useEffect, useState} from 'react'
import {Outlet, NavLink, useNavigate} from 'react-router-dom'
import {useAuth} from '../../app/modules/auth'
import {PageDataProvider} from './core'
import './admin.css'

const ACCENT = '#039540'

type NavEntry =
  | {type: 'label'; label: string}
  | {type: 'link'; to: string; label: string; icon: ReactNode}

const ic = (children: ReactNode): ReactNode => (
  <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    {children}
  </svg>
)

// Crew Crashpad admin navigation, grouped per the build plan. Order mirrors the
// routes in PrivateRoutes; upcoming modules land on the "coming soon" page.
const NAV: NavEntry[] = [
  {type: 'link', to: '/dashboard', label: 'Dashboard', icon: ic(<><rect x='3' y='3' width='7' height='7'/><rect x='14' y='3' width='7' height='7'/><rect x='3' y='14' width='7' height='7'/><rect x='14' y='14' width='7' height='7'/></>)},

  {type: 'label', label: 'People'},
  {type: 'link', to: '/applications', label: 'Applications', icon: ic(<><path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'/><polyline points='14 2 14 8 20 8'/><circle cx='12' cy='13' r='2'/><path d='M9 18a3 3 0 0 1 6 0'/></>)},
  {type: 'link', to: '/accounts', label: 'Accounts', icon: ic(<><path d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2'/><circle cx='12' cy='7' r='4'/></>)},

  {type: 'label', label: 'Operations'},
  {type: 'link', to: '/listings', label: 'Rooms', icon: ic(<><path d='M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'/><path d='M9 22V12h6v10'/></>)},
  {type: 'link', to: '/beds', label: 'Beds', icon: ic(<><path d='M2 17v-5a2 2 0 0 1 2-2h13a3 3 0 0 1 3 3v4'/><path d='M2 17h20'/><path d='M2 11V7'/><circle cx='7' cy='10' r='1.5'/></>)},
  {type: 'link', to: '/bed-types', label: 'Bed Types', icon: ic(<><path d='M3 7V5a2 2 0 0 1 2-2h2'/><path d='M17 3h2a2 2 0 0 1 2 2v2'/><path d='M21 17v2a2 2 0 0 1-2 2h-2'/><path d='M7 21H5a2 2 0 0 1-2-2v-2'/><line x1='7' y1='12' x2='17' y2='12'/><line x1='12' y1='7' x2='12' y2='17'/></>)},
  {type: 'link', to: '/bookings', label: 'Bookings & Waitlist', icon: ic(<><rect x='3' y='4' width='18' height='18' rx='2'/><line x1='16' y1='2' x2='16' y2='6'/><line x1='8' y1='2' x2='8' y2='6'/><path d='M9 15l2 2 4-4'/></>)},
  {type: 'link', to: '/payments', label: 'Payments', icon: ic(<><rect x='2' y='5' width='20' height='14' rx='2'/><line x1='2' y1='10' x2='22' y2='10'/></>)},
  {type: 'link', to: '/payment-settings', label: 'Payment Settings', icon: ic(<><rect x='2' y='5' width='20' height='14' rx='2'/><line x1='2' y1='10' x2='22' y2='10'/><circle cx='7' cy='15' r='1.3'/></>)},
  {type: 'link', to: '/requests', label: 'Cleaning & Maintenance', icon: ic(<path d='M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.3 2.3-2-2 2.3-2.3z'/>)},
  {type: 'link', to: '/cleaner', label: 'Cleaner', icon: ic(<><path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2'/><circle cx='9' cy='7' r='4'/><polyline points='16 11 18 13 22 9'/></>)},

  {type: 'label', label: 'Content'},
  {type: 'link', to: '/announcements', label: 'Announcements', icon: ic(<><path d='M3 11l18-5v12L3 13v-2z'/><path d='M11.6 16.8a3 3 0 1 1-5.8-1.6'/></>)},
  {type: 'link', to: '/door-codes', label: 'Door Codes', icon: ic(<><rect x='3' y='11' width='18' height='11' rx='2'/><path d='M7 11V7a5 5 0 0 1 10 0v4'/><circle cx='12' cy='16' r='1'/></>)},
  {type: 'link', to: '/info-pages', label: 'Info Pages', icon: ic(<><path d='M4 19.5A2.5 2.5 0 0 1 6.5 17H20'/><path d='M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z'/></>)},
  {type: 'link', to: '/site-gallery', label: 'Site Gallery', icon: ic(<><rect x='3' y='3' width='18' height='18' rx='2'/><circle cx='8.5' cy='8.5' r='1.5'/><path d='M21 15l-5-5L5 21'/></>)},
  {type: 'link', to: '/welcome-email', label: 'Emails', icon: ic(<><rect x='2' y='4' width='20' height='16' rx='2'/><path d='M22 7l-10 6L2 7'/></>)},
  {type: 'link', to: '/flyer', label: 'Flyer Automation', icon: ic(<><rect x='3' y='3' width='18' height='18' rx='2'/><circle cx='8.5' cy='8.5' r='1.5'/><path d='M21 15l-5-5L5 21'/></>)},
  {type: 'link', to: '/integrations', label: 'Integrations', icon: ic(<><path d='M6 3v6a6 6 0 0 0 12 0V3'/><path d='M4 9h4M16 9h4M12 15v6'/></>)},

  {type: 'label', label: 'Account'},
  {type: 'link', to: '/audit-log', label: 'Audit log', icon: ic(<><path d='M9 3h6a1 1 0 0 1 1 1v2H8V4a1 1 0 0 1 1-1z'/><path d='M8 5H6a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2'/><line x1='8' y1='12' x2='16' y2='12'/><line x1='8' y1='16' x2='13' y2='16'/></>)},
]

const MOBILE_QUERY = '(max-width: 900px)'

const MasterLayout = () => {
  const {currentUser, logout} = useAuth()
  const navigate = useNavigate()

  // Responsive: on phones/tablets the sidebar becomes an off-canvas drawer.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches,
  )
  const [navOpen, setNavOpen] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY)
    const onChange = () => {
      setIsMobile(mq.matches)
      if (!mq.matches) setNavOpen(false)
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const closeNav = () => setNavOpen(false)

  const initials =
    ((currentUser?.first_name?.[0] ?? '') + (currentUser?.last_name?.[0] ?? '')).toUpperCase() || 'AD'

  const handleLogout = () => {
    logout()
    navigate('/auth/login')
  }

  return (
    <PageDataProvider>
      <div style={{display: 'flex', height: '100vh', background: '#f8fafc', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'}}>

        {/* ── Mobile backdrop ── */}
        {isMobile && navOpen && (
          <div onClick={closeNav} style={{position: 'fixed', inset: 0, background: 'rgba(8,10,14,0.45)', zIndex: 150}} />
        )}

        {/* ── Sidebar (off-canvas drawer on mobile) ── */}
        <aside style={{
          width: 260,
          flexShrink: 0,
          background: '#ffffff',
          borderRight: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          zIndex: isMobile ? 200 : 100,
          overflowY: 'auto',
          transform: isMobile && !navOpen ? 'translateX(-100%)' : 'translateX(0)',
          transition: 'transform 0.25s ease',
          boxShadow: isMobile && navOpen ? '0 12px 40px rgba(0,0,0,0.28)' : 'none',
        }}>
          {/* Logo */}
          <div style={{padding: '24px 20px 20px', borderBottom: '1px solid #f1f5f9'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
              <img
                src='/app-icon.png'
                alt='Crew Crashpad'
                style={{width: 36, height: 36, borderRadius: 8, objectFit: 'cover'}}
              />
              <div style={{lineHeight: 1.1}}>
                <div style={{fontSize: 15, fontWeight: 700, color: '#1e293b'}}>Crew Crashpad</div>
                <div style={{fontSize: 11, color: '#94a3b8'}}>Admin</div>
              </div>
            </div>
          </div>

          {/* Nav items */}
          <nav style={{flex: 1, padding: '12px 12px'}}>
            {NAV.map((item, i) =>
              item.type === 'label' ? (
                <div
                  key={`label-${i}`}
                  style={{
                    padding: '16px 12px 6px',
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: '#94a3b8',
                  }}
                >
                  {item.label}
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => isMobile && setNavOpen(false)}
                  style={({isActive}) => ({
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '9px 12px',
                    borderRadius: 8,
                    marginBottom: 2,
                    fontSize: 14,
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? ACCENT : '#64748b',
                    background: isActive ? 'rgba(3,149,64,0.08)' : 'transparent',
                    textDecoration: 'none',
                    transition: 'all 0.15s',
                  })}
                >
                  {item.icon}
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          {/* Logout */}
          <div style={{padding: '12px 12px', borderTop: '1px solid #f1f5f9'}}>
            <button
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '9px 12px',
                borderRadius: 8,
                width: '100%',
                fontSize: 14,
                fontWeight: 400,
                color: '#ef4444',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                <path d='M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4'/>
                <polyline points='16 17 21 12 16 7'/>
                <line x1='21' y1='12' x2='9' y2='12'/>
              </svg>
              Logout
            </button>
          </div>
        </aside>

        {/* ── Main area ── */}
        <div style={{marginLeft: isMobile ? 0 : 260, flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, width: isMobile ? '100%' : 'auto'}}>

          {/* Topbar */}
          <header style={{
            height: 64,
            background: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMobile ? 'space-between' : 'flex-end',
            padding: '0 16px',
            position: 'sticky',
            top: 0,
            zIndex: 50,
            flexShrink: 0,
          }}>
            {isMobile && (
              <button
                onClick={() => setNavOpen((v) => !v)}
                aria-label='Toggle menu'
                style={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', color: '#1e293b'}}
              >
                <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'>
                  <line x1='3' y1='6' x2='21' y2='6' /><line x1='3' y1='12' x2='21' y2='12' /><line x1='3' y1='18' x2='21' y2='18' />
                </svg>
              </button>
            )}
            <NavLink to='/settings' title='Account settings' style={{display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', cursor: 'pointer'}}>
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #039540, #027a34)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 13,
                  flexShrink: 0,
                }}
              >
                {initials}
              </div>
              <div>
                <div style={{fontSize: 13, fontWeight: 600, color: '#1e293b', lineHeight: 1.2}}>
                  {currentUser?.first_name} {currentUser?.last_name}
                </div>
                <div style={{fontSize: 12, color: '#94a3b8'}}>
                  {currentUser?.email}
                </div>
              </div>
            </NavLink>
          </header>

          {/* Content */}
          <main className='ccp-admin' style={{flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: isMobile ? '18px 14px' : '28px 28px'}}>
            <Outlet />
          </main>
        </div>
      </div>
    </PageDataProvider>
  )
}

export {MasterLayout}
